import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ImageOff, Loader2, Lock, Pencil, Plus, Tags, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { CategoryDialog } from "@/components/categories/CategoryDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCategories, useDeleteCategory, useReorderCategories } from "@/hooks/use-categories";
import { usePagination } from "@/hooks/use-pagination";
import { describeCallableError } from "@/lib/callables";
import { formatNumber } from "@/lib/format";
import { DEFAULT_PRODUCT_TYPE_ID, DEFAULT_PRODUCT_TYPE_NAME } from "@/lib/productTypes";
import type { Category } from "@/types";

/**
 * Tipos de produto (a coleção continua `categories`).
 *
 * Servem à busca do app do cliente: o vendedor escolhe o tipo de cada produto,
 * e o cliente procura por eles. O cardápio do estabelecimento não sai daqui
 * (são as seções do vendedor). O tipo padrão, "Outros", não existe na coleção:
 * aparece num cartão fixo, fora da tabela, da paginação e da reordenação.
 */
export default function Categories() {
  const categories = useCategories();
  const remove = useDeleteCategory();
  const reorder = useReorderCategories();

  const [editing, setEditing] = useState<Category | undefined>();
  const [creating, setCreating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const rows = useMemo(() => categories.data ?? [], [categories.data]);
  const nextOrder = useMemo(
    () => (rows.length ? Math.max(...rows.map((c) => c.order ?? 0)) + 1 : 0),
    [rows],
  );
  const removingTarget = rows.find((c) => c.id === removingId);
  const pagination = usePagination(rows);
  // As setas movem pela posição na lista INTEIRA, e não na página: subir a
  // primeira linha da página 2 a leva para o fim da página 1.
  const offset = pagination.paginationProps.rangeStart - 1;

  /**
   * Mover troca a posição com o vizinho e regrava a ordem INTEIRA.
   *
   * Regravar tudo em vez de só os dois documentos é deliberado: o campo `order`
   * dos documentos antigos tem buracos e repetições, e uma troca de pares
   * preservaria a bagunça. Uma renumeração completa deixa a lista consistente a
   * cada movimento.
   */
  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;

    const next = [...rows];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);

    try {
      await reorder.mutateAsync(next.map((c, i) => ({ id: c.id, order: i })));
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  async function confirmRemove() {
    if (!removingId) return;
    try {
      await remove.mutateAsync(removingId);
      toast.success("Tipo de produto removido.");
      setRemovingId(null);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <>
      <PageHeader
        title="Tipos de produto"
        description="Usados na busca do aplicativo do cliente. O vendedor escolhe o tipo de cada produto, e o produto sem tipo entra em Outros."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> Novo tipo
          </Button>
        }
      />

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
            <Lock className="size-4 text-muted-foreground" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{DEFAULT_PRODUCT_TYPE_NAME}</span>
              <Badge>Padrão</Badge>
              <span className="font-mono text-caption text-muted-foreground">
                {DEFAULT_PRODUCT_TYPE_ID}
              </span>
            </div>
            <p className="text-small text-muted-foreground">
              Os produtos sem tipo definido entram aqui, e ele aparece sempre no fim da busca do
              aplicativo do cliente. É fixo do projeto: não pode ser editado, reordenado nem
              removido.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {categories.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Tags}
              title="Nenhum tipo de produto cadastrado"
              description="Sem tipos, todo produto aparece em Outros na busca do aplicativo do cliente."
              action={
                <Button onClick={() => setCreating(true)}>
                  <Plus /> Criar a primeira
                </Button>
              }
            />
          ) : (
            <Table className="table-stacked">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Ordem</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="hidden lg:table-cell">Produtos</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((category, pageIndex) => {
                  const index = offset + pageIndex;
                  return (
                  <TableRow key={category.id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-9"
                          aria-label={`Mover ${category.name} para cima`}
                          disabled={index === 0 || reorder.isPending}
                          onClick={() => void move(index, -1)}
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-9"
                          aria-label={`Mover ${category.name} para baixo`}
                          disabled={index === rows.length - 1 || reorder.isPending}
                          onClick={() => void move(index, 1)}
                        >
                          <ArrowDown />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="cell-title">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
                          {category.imageUrl ? (
                            <img
                              src={category.imageUrl}
                              alt=""
                              loading="lazy"
                              className="size-full object-cover"
                            />
                          ) : (
                            <ImageOff className="size-4 text-muted-foreground" aria-label="Sem imagem" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <span className="font-semibold">{category.name || "Sem nome"}</span>
                          {category.slug ? (
                            <p className="truncate font-mono text-caption text-muted-foreground">
                              {category.slug}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell tabular-nums">
                      {category.productsCount != null ? formatNumber(category.productsCount) : "-"}
                    </TableCell>
                    <TableCell>
                      {category.isActive ? (
                        <Badge variant="ok">Ativo</Badge>
                      ) : (
                        <Badge variant="muted">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="cell-actions">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(category)}
                          aria-label={`Editar ${category.name}`}
                        >
                          <Pencil /> Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => setRemovingId(category.id)}
                          aria-label={`Remover ${category.name}`}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <Pagination {...pagination.paginationProps} itemLabel="tipos" />
        </CardContent>
      </Card>

      {creating ? (
        <CategoryDialog nextOrder={nextOrder} open onOpenChange={() => setCreating(false)} />
      ) : null}

      {editing ? (
        <CategoryDialog
          category={editing}
          nextOrder={nextOrder}
          open
          onOpenChange={() => setEditing(undefined)}
        />
      ) : null}

      <AlertDialog open={Boolean(removingId)} onOpenChange={(open) => !open && setRemovingId(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>Remover o tipo {removingTarget?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Só é possível remover um tipo que nenhum produto usa: com produto, a remoção é recusada.
            Se a intenção é só tirá-lo da busca do aplicativo do cliente, prefira desativá-lo. Esta
            ação não tem desfazer.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmRemove()} disabled={remove.isPending}>
              {remove.isPending ? <Loader2 className="animate-spin" /> : null}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
